import { setNestedObjectValues, useFormikContext } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getClient } from "@/features/clients/api";
import type { Client } from "@/features/clients/types";
import { SendESignDialog } from "@/features/esignatures/components/send-esign-dialog";
import { previewSubmissionPdf, saveSubmissionData } from "@/features/forms/api";
import type { FormStatus, FormSubmission } from "@/features/forms/types";
import { ApiError } from "@/lib/api";
import { ResizeLogoDialog, SignatureDialog } from "./branding-dialogs";
import { SaveToDocumentsDialog } from "./save-to-documents-dialog";

// Per-field autosave debounce (legacy fired on blur/change with no interval; we
// debounce for UX and the server merges the whole blob).
const AUTOSAVE_DELAY_MS = 800;

function errorMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

interface Props {
  submission: FormSubmission;
  clientId: string;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Shared action bar for every form editor (rendered inside `<Formik>`): per-field
 * debounced autosave, explicit Save, Preview, Save-to-Documents (name/folder/
 * overwrite modal), the firm logo/signature modals, and Get-e-signature (sends
 * the generated PDF via SendESignDialog). Reads the live form values from Formik
 * context so it works for any form's payload shape.
 */
export function FormEditorActions({ submission, clientId, onSubmissionChange }: Props) {
  const { values, validateForm, setTouched, setValues } =
    useFormikContext<Record<string, unknown>>();
  const [busy, setBusy] = useState<"save" | "preview" | null>(null);
  const [autosaveState, setAutosaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [docsOpen, setDocsOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);
  // The e-sign dialog needs the full Client; the editor only has the id, so it's
  // fetched lazily the first time the user sends for signature.
  const [esignClient, setEsignClient] = useState<Client | null>(null);
  const [esignBusy, setEsignBusy] = useState(false);

  // A COMPLETED (already filed) form isn't downgraded back to DRAFT on save.
  const savedStatus: FormStatus | undefined =
    submission.status === "COMPLETED" ? undefined : "DRAFT";

  // The last-persisted serialized blob. Initialized on first render (below) to
  // the normalized Formik values — not the raw submission.data — so the initial
  // default-filling doesn't fire a spurious autosave on mount.
  const lastSavedRef = useRef<string | null>(null);
  // Pending autosave timer + an in-flight guard so autosave never races an
  // explicit Save/Preview/generate: any explicit persist cancels the pending
  // timer, and the timer skips if a persist is already in flight.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // This bar is reused across clients (same /irs-forms/:formNumber route, no
  // remount on :id change), so drop the cached client + close the dialog when
  // the client changes — never pre-fill a signature request with a stale client.
  useEffect(() => {
    setEsignClient(null);
    setEsignOpen(false);
  }, [clientId]);

  const persist = useCallback(
    async (status?: FormStatus) => {
      // Cancel any queued autosave — this write supersedes it.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const serialized = JSON.stringify(values);
      savingRef.current = true;
      try {
        const updated = await saveSubmissionData(submission.id, values, status);
        lastSavedRef.current = serialized;
        if (mountedRef.current) onSubmissionChange(updated);
        return updated;
      } finally {
        savingRef.current = false;
      }
    },
    [values, submission.id, onSubmissionChange],
  );

  // Debounced per-field autosave: whenever the values change and differ from the
  // last persisted blob, save after a short idle. The first render just captures
  // the baseline (no save).
  useEffect(() => {
    const serialized = JSON.stringify(values);
    if (lastSavedRef.current === null) {
      lastSavedRef.current = serialized;
      return;
    }
    if (serialized === lastSavedRef.current) return;
    setAutosaveState("saving");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      // An explicit Save/Preview is mid-flight; it persists the latest values.
      if (savingRef.current) return;
      try {
        await persist(savedStatus);
        if (mountedRef.current) setAutosaveState("saved");
      } catch {
        if (mountedRef.current) setAutosaveState("idle");
      }
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [values, persist, savedStatus]);

  // Run Formik validation; on any error, flag the fields + toast and return
  // false. Shared by Save, Preview, and Save-to-Documents so a form can never be
  // generated/previewed with validation errors the user hasn't seen inline.
  async function validateOrFlag(): Promise<boolean> {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      // Deep-mark every errored field (incl. nested array rows like
      // matters[0].years) touched so the inline errors actually render.
      setTouched(setNestedObjectValues(errors, true), false);
      toast.error("Please fix the highlighted fields.");
      return false;
    }
    return true;
  }

  // Reset every editable field to blank (recursively) — strings → "", numbers →
  // "", booleans → false, arrays → []. Does not auto-save; the user chooses to
  // Save the cleared form (or navigate away) afterward.
  function clearValue(v: unknown): unknown {
    if (Array.isArray(v)) return [];
    if (v && typeof v === "object") {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, val]) => [k, clearValue(val)]),
      );
    }
    if (typeof v === "boolean") return false;
    return "";
  }

  function handleClear() {
    if (!window.confirm("Clear every field on this form? This can't be undone.")) return;
    setValues(clearValue(values) as Record<string, unknown>);
    toast.success("Form cleared");
  }

  async function handleSave() {
    if (!(await validateOrFlag())) return;
    setBusy("save");
    try {
      await persist(savedStatus);
      toast.success("Form saved");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save the form."));
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  }

  async function handlePreview() {
    // Open the tab synchronously (inside the click) so popup blockers don't
    // kill it after the fetch; close it if validation then fails.
    const previewWindow = window.open("", "_blank");
    if (!(await validateOrFlag())) {
      previewWindow?.close();
      return;
    }
    setBusy("preview");
    try {
      await persist(savedStatus);
      await previewSubmissionPdf(submission.id, previewWindow);
    } catch (err) {
      previewWindow?.close();
      toast.error(errorMessage(err, "Couldn't generate the preview."));
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  }

  async function openSaveToDocuments() {
    if (!(await validateOrFlag())) return;
    // Persist the latest edits so the generate fills from fresh data.
    setBusy("save");
    try {
      await persist(savedStatus);
      setDocsOpen(true);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save the form."));
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  }

  async function openESignature() {
    // The signed artifact is the generated PDF; require it before sending.
    if (!submission.generatedDocument) {
      toast.info("Save the form to Documents first, then send it for e-signature.");
      setDocsOpen(true);
      return;
    }
    setEsignBusy(true);
    try {
      const client = esignClient ?? (await getClient(clientId));
      if (!esignClient) setEsignClient(client);
      setEsignOpen(true);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load the client."));
    } finally {
      if (mountedRef.current) setEsignBusy(false);
    }
  }

  return (
    <>
      <div className="bg-card sticky bottom-0 z-10 -mx-6 -mb-6 mt-6 flex flex-wrap items-center gap-3 border-t px-6 py-4">
        <span className="text-muted-foreground mr-auto text-xs" aria-live="polite">
          {autosaveState === "saving"
            ? "Saving…"
            : autosaveState === "saved"
              ? "All changes saved"
              : ""}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => setLogoOpen(true)}>
          Resize logo
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setSignatureOpen(true)}
        >
          Signatures
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy !== null || esignBusy}
          onClick={openESignature}
        >
          {esignBusy ? "Preparing…" : "Get e-signature"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy !== null}
          onClick={handleClear}
        >
          Clear form
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy !== null}
          onClick={handlePreview}
        >
          {busy === "preview" ? "Preparing…" : "Preview PDF"}
        </Button>
        <Button type="button" variant="outline" disabled={busy !== null} onClick={openSaveToDocuments}>
          Save to Documents
        </Button>
        <Button type="button" disabled={busy !== null} onClick={handleSave}>
          {busy === "save" ? "Saving…" : "Save"}
        </Button>
      </div>

      <SaveToDocumentsDialog
        open={docsOpen}
        onOpenChange={setDocsOpen}
        submission={submission}
        clientId={clientId}
        onSaved={onSubmissionChange}
      />
      <ResizeLogoDialog open={logoOpen} onOpenChange={setLogoOpen} />
      <SignatureDialog open={signatureOpen} onOpenChange={setSignatureOpen} />
      {esignClient && submission.generatedDocument && (
        <SendESignDialog
          open={esignOpen}
          onOpenChange={setEsignOpen}
          client={esignClient}
          document={{
            id: submission.generatedDocument.id,
            name: submission.generatedDocument.name,
          }}
        />
      )}
    </>
  );
}
